// RICEVisualization - RICE ranked list and bar chart visualization
// Implements US-5.1: RICE Ranked List and Bar Chart

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Item } from '../../types/domain';
import styles from './RICEVisualization.module.css';

interface RICEVisualizationProps {
  items: Item[];
}

/**
 * RICE result with calculated score and raw criteria
 */
interface RICEResult {
  itemId: string;
  title: string;
  score: number;
  reach: number;
  impact: number;
  confidence: number;
  effort: number;
  rank: number;
}

/**
 * CALCULATE RICE score for an item
 * Formula: (Reach × Impact × Confidence) / Effort
 */
const calculateRICEScore = (item: Item): RICEResult | null => {
  // EXTRACT scores from item
  const reach = Number(item.scores?.find((s) => s.criterion === 'reach')?.value || 0);
  const impact = Number(item.scores?.find((s) => s.criterion === 'impact')?.value || 0);
  const confidence = Number(item.scores?.find((s) => s.criterion === 'confidence')?.value || 0);
  const effort = Number(item.scores?.find((s) => s.criterion === 'effort')?.value || 0);

  // VALIDATE: Only calculate if all criteria are scored
  if (reach === 0 || impact === 0 || confidence === 0 || effort === 0) {
    return null;
  }

  // CALCULATE RICE score
  const score = (reach * impact * confidence) / effort;

  return {
    itemId: item.id,
    title: item.title,
    score: Math.round(score * 100) / 100, // Round to 2 decimal places
    reach,
    impact,
    confidence,
    effort,
    rank: 0, // Will be set after sorting
  };
};

/**
 * GET color for bar based on rank
 * NOTE: Top items get green, middle items get yellow/orange, lower items get red
 */
const getBarColor = (rank: number, totalScored: number): string => {
  const percentage = rank / totalScored;

  if (percentage <= 0.33) {
    return '#10b981'; // Green for top third
  } else if (percentage <= 0.67) {
    return '#f59e0b'; // Orange for middle third
  } else {
    return '#ef4444'; // Red for bottom third
  }
};

/**
 * RICE VISUALIZATION component
 * - Displays ranked list of scored items
 * - Displays bar chart of RICE scores
 * - Shows unscored items at bottom
 * - Shows empty state when no items scored
 */
export const RICEVisualization: React.FC<RICEVisualizationProps> = ({ items }) => {
  /**
   * PROCESS items and calculate scores
   * NOTE: Memoized to avoid recalculating on every render
   */
  const { scoredResults, unscoredItems } = useMemo(() => {
    const scored: RICEResult[] = [];
    const unscored: Item[] = [];

    // SEPARATE scored and unscored items
    items.forEach((item) => {
      const result = calculateRICEScore(item);
      if (result) {
        scored.push(result);
      } else {
        unscored.push(item);
      }
    });

    // SORT scored items by score (highest first) and assign ranks
    scored.sort((a, b) => b.score - a.score);
    scored.forEach((result, index) => {
      result.rank = index + 1;
    });

    return { scoredResults: scored, unscoredItems: unscored };
  }, [items]);

  /**
   * PREPARE chart data for Recharts
   * NOTE: Keep descending order so highest scores appear at top
   */
  const chartData = useMemo(() => {
    return scoredResults.map((result) => ({
      title: result.title.length > 30 ? result.title.substring(0, 30) + '...' : result.title,
      score: result.score,
      rank: result.rank,
    }));
  }, [scoredResults]);

  // RENDER empty state if no items are scored
  if (scoredResults.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <h3>Füge Themen / Elemente und Bewertungen hinzu, um Ergebnisse zu sehen</h3>
        <p>Bewerte mindestens ein Thema / Element, um die RICE-Visualisierung zu sehen</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* RANKED LIST SECTION */}
      <div className={styles.rankedList}>
        
        <div className={styles.listHeader}>
          <span className={styles.rankColumn}>Rang</span>
          <span className={styles.titleColumn}>Thema / Element</span>
          <span className={styles.scoreColumn}>Score</span>
          <span className={styles.criteriaColumn}>R</span>
          <span className={styles.criteriaColumn}>I</span>
          <span className={styles.criteriaColumn}>C</span>
          <span className={styles.criteriaColumn}>E</span>
        </div>
        <div className={styles.listItems}>
          {scoredResults.map((result) => (
            <div key={result.itemId} className={styles.listItem}>
              <span className={styles.rankColumn}>
                <span className={styles.rankBadge}>#{result.rank}</span>
              </span>
              <span className={styles.titleColumn} title={result.title}>
                {result.title}
              </span>
              <span className={styles.scoreColumn}>
                <span className={styles.scoreBadge}>{result.score}</span>
              </span>
              <span className={styles.criteriaColumn}>{result.reach}</span>
              <span className={styles.criteriaColumn}>{result.impact}</span>
              <span className={styles.criteriaColumn}>{result.confidence}</span>
              <span className={styles.criteriaColumn}>{result.effort}</span>
            </div>
          ))}
        </div>

        {/* UNSCORED ITEMS - Show at bottom if any */}
        {unscoredItems.length > 0 && (
          <div className={styles.unscoredSection}>
            <h4 className={styles.unscoredTitle}>
              Nicht bewertete Themen / Elemente ({unscoredItems.length})
            </h4>
            <div className={styles.unscoredList}>
              {unscoredItems.map((item) => (
                <div key={item.id} className={styles.unscoredItem}>
                  <span className={styles.unscoredIcon}>○</span>
                  <span className={styles.unscoredItemTitle}>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* BAR CHART SECTION */}
      <div className={styles.chartSection}>
        <h3 className={styles.sectionTitle}>Score-Verteilung</h3>
        <ResponsiveContainer width="100%" height={Math.max(300, scoredResults.length * 40)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              dataKey="title"
              type="category"
              width={150}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number) => [`Score: ${value}`, '']}
              labelFormatter={(label: string) => {
                // FIND full title for tooltip
                const result = scoredResults.find(
                  (r) => r.title.startsWith(label.replace('...', ''))
                );
                return result ? result.title : label;
              }}
            />
            <Bar dataKey="score" radius={[0, 0, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.rank, scoredResults.length)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* LEGEND */}
        <div className={styles.chartLegend}>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#10b981' }}></span>
            <span>Höchste Priorität</span>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#f59e0b' }}></span>
            <span>Mittlere Priorität</span>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#ef4444' }}></span>
            <span>Niedrigere Priorität</span>
          </div>
        </div>
      </div>
    </div>
  );
};
